import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCcw } from 'lucide-react';

interface AppUpdatePopupProps {
  isOpen: boolean;
  onUpdate: () => void;
  version?: string;
}

const ICON_URL = '/images/bot-img.webp';

export const AppUpdatePopup: React.FC<AppUpdatePopupProps> = ({
  isOpen,
  onUpdate,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-9999 flex items-center justify-center p-4">

          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Outer wrapper with 3D perspective */}
          <motion.div
            initial={{ opacity: 0, y: 50, rotateX: 12, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.93 }}
            transition={{ type: 'spring', stiffness: 280, damping: 24 }}
            style={{ perspective: '1200px' }}
            className="relative w-full max-w-lg flex flex-col items-center"
          >

            {/* ── Floating Icon above card ── */}
            <motion.div
              initial={{ y: -20, opacity: 0, scale: 0.6 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 240, damping: 18, delay: 0.18 }}
              className="relative z-20 mb-[-48px]"
            >
              {/* Deep glow underneath disc */}
              <div
                className="absolute inset-0 rounded-full blur-2xl scale-[1.6] opacity-70"
                style={{ background: 'radial-gradient(circle, #818cf8 0%, #6366f100 70%)' }}
              />
              {/* 3D disc — static circle */}
              <div
                className="relative w-24 h-24 rounded-full flex items-center justify-center"
                style={{
                  background: 'linear-gradient(160deg, #ffffff 0%, #e0e7ff 45%, #c7d2fe 100%)',
                  boxShadow: [
                    '0 15px 35px -10px rgba(99,102,241,0.5)',
                    'inset 0 -4px 6px -2px rgba(165,180,252,0.6)'
                  ].join(', ')
                }}
              >
                {/* Inner ring for depth */}
                <div
                  className="absolute inset-[6px] rounded-full"
                  style={{
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.6) 0%, rgba(199,210,254,0.2) 100%)',
                    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.8)',
                  }}
                />

                {/* ── Animated image: bobs up/down + gentle tilt ── */}
                <motion.img
                  animate={{
                    y: [0, -6, 0],
                    scale: [1, 1.02, 1],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  src={ICON_URL}
                  alt="Update"
                  className="relative w-[62px] h-[62px] object-contain drop-shadow-lg"
                />
              </div>
            </motion.div>

            {/* ── Main Card ── */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="relative w-full rounded-xl overflow-hidden"
              style={{
                background: 'linear-gradient(175deg, #f8faff 0%, #ffffff 60%)',
                boxShadow: [
                  '0 32px 64px rgba(99,102,241,0.18)',
                  '0 16px 32px rgba(99,102,241,0.12)',
                  '0 4px 8px rgba(99,102,241,0.08)',
                  '0 1px 0 rgba(255,255,255,1) inset',
                  '0 -1px 0 rgba(199,210,254,0.5) inset',
                ].join(', '),
                border: '1px solid rgba(199,210,254,0.6)',
              }}
            >
              {/* Card top gradient band */}
              <div
                className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(224,231,255,0.5) 0%, rgba(255,255,255,0) 100%)' }}
              />

              <div className="relative pt-16 pb-7 px-7 flex flex-col items-center text-center">

                {/* Heading */}
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.28 }}
                  className="text-3xl font-bold mb-2"
                  style={{
                    background: 'linear-gradient(135deg, #1e1b4b 0%, #4338ca 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '-0.3px',
                  }}
                >
                  Update Available!
                </motion.h2>

                {/* Description */}
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.34 }}
                  className="text-slate-500 leading-relaxed mb-4"
                >
                  An exciting new update is ready for you! Enjoy new features, improved performance, and fully upgraded functionalities. 
                  <br />
                  <span className='font-bold'>Click 'Update Now' to get started.</span>
                </motion.p>

                {/* ── 3D Button ── */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="w-full"
                >
                  <motion.button
                    onClick={onUpdate}
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 2, scale: 0.98 }}
                    className="py-2 px-4 rounded-md text-white font-bold text-sm tracking-wide relative overflow-hidden"
                    style={{
                      background: 'linear-gradient(160deg, #5b8eff 0%, #3967fe 50%, #2d55d6 100%)',
                      boxShadow: [
                        '0 1px 0 rgba(255,255,255,0.25) inset',    // top highlight
                        '0 -1px 0 rgba(0,0,0,0.15) inset',          // bottom inner shadow
                        '0 6px 0 #1e3fad',                           // 3D bottom "thickness"
                        '0 8px 20px rgba(57,103,254,0.45)',          // main glow
                        '0 3px 8px rgba(57,103,254,0.3)',            // close glow
                      ].join(', '),
                    }}
                  >
                    {/* Shine overlay */}
                    <span
                      className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                      style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 100%)' }}
                    />
                    <span className="relative flex items-center justify-center gap-2">
                      <RefreshCcw className="w-4 h-4" />
                      Update Now
                    </span>
                  </motion.button>
                </motion.div>


              </div>
            </motion.div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
